import {
  faClipboardList,
  faCodeMerge,
  faPrescriptionBottle,
  faSort,
  faUserSecret,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notification } from "antd";
import {
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { Rings } from "react-loader-spinner";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate } from "react-router-dom";
import Checkbox from "../../../components/Checkbox";
import DeviceRouteSelector from "../../../components/DeviceRouteSelector/DeviceRouteSelector";
import FacilityDispensingOrdersCard from "../../../components/FacilityDispensingOrdersCard2";
import MultiSelectDropDown from "../../../components/MultiSelectDropDown/MultiSelectDropDown";
import TrayPickList from "../../../components/TrayPickList/TrayPickList";
import { ScreenContext } from "../../../contexts/ScreenContext";
import ProductFieldController from "../../../controllers/ProductFieldController";
import ConfigService from "../../../services/ConfigService";
import DispensingOrderService from "../../../services/DispensingOrderService";
import PharmacyService from "../../../services/PharmacyService";
import RoleCheckService from "../../../services/RoleCheckService";
import DeviceAccessService from "../../../services/DeviceAccessService";
import StockLocationService from "../../../services/StockLocationService";
import ModalGen from "../../commonUI/ModalGen";
import DeviceCoverage from "../DeviceCoverage/DeviceCoverage";
import PackagingMethodFilter from "../PackagingMethodFilter/PackagingMethodFilter";
import PickList from "../PickList/PickList";
import SelectDevice from "../PickList/SelectDevice";
import PrintMedList from "../PrintMedList/PrintMedList";
import SiteHeader from "../SiteHeader/SiteHeader";
import styles from "./DispensingOrders2.module.css";
import DispensingOrdersDesktopView from "./DispensingOrdersDesktopView";
import DispensingOrdersMobileView from "./DispensingOrdersMobileView";

const DispensingOrders = ({ user, dispensingOrdersReturnState }) => {
  const { smallScreen } = useContext(ScreenContext);
  const smScreen = useMediaQuery(smallScreen);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("dispensingOrders");
  const dialogRef = useRef(null);
  const blockRef = useRef(false);
  const routingRef = useRef(false);
  const refreshingRef = useRef(false);
  const userPackagingTypesRef = useRef(new Set());
  const productFieldControllerRef = useRef(new ProductFieldController());
  const facilitiesEl = useRef();

  const [sites, setSites] = useState([]);
  const [selectedSiteIndex, setSelectedSiteIndex] = useState(null);
  const [fetchingPending, setFetchingPending] = useState(false);
  const [fetchingRouted, setFetchingRouted] = useState(false);
  const [fetchingPackaged, setFetchingPackaged] = useState(false);
  const [trayData, setTrayData] = useState([]);

  const [pendingDispensingOrders, setPendingDispensingOrders] = useState([]);
  const [routedDispensingOrders, setRoutedDispensingOrders] = useState([]);
  const [joinedRoutedDispensingOrders, setJoinedRoutedDispensingOrders] =
    useState([]);
  const [packagedDispensingOrders, setPackagedDispensingOrders] = useState([]);
  const [joinedPackagedDispensingOrders, setJoinedPackagedDispensingOrders] =
    useState([]);
  const [error, setError] = useState("");
  const [routedDevices, setRoutedDevices] = useState([]);
  const [routedDevicesSelection, setRoutedDevicesSelection] = useState([]);
  const [packagedDevices, setPackagedDevices] = useState([]);
  const [packagedDevicesSelection, setPackagedDevicesSelection] = useState([]);
  const [pendingDOSelection, setPendingDOSelection] = useState([]);
  const [printDispensingOrder, setPrintDispensingOrder] = useState(null);
  const [printPatients, setPrintPatients] = useState([]);
  const [printMedLists, setPrintMedLists] = useState([]);
  const [routedDOSelection, setRoutedDOSelection] = useState([]);
  const [packagedDOSelection, setPackagedDOSelection] = useState([]);
  const [verifiedDOSelection, setVerifiedDOSelection] = useState([]);
  const [selectAllPendingOrders, setSelectAllPendingOrders] = useState(false);
  const [orderDetailsDialog, setOrderDetailsDialog] = useState(null);
  const [showDeviceRouteSelector, setShowDeviceRouteSelector] = useState(null);
  const [draggedDispensingOrders, setDraggedDispensingOrders] = useState([]);
  const [selectedPackagingMethods, setSelectedPackagingMethods] = useState([]);
  const [packagingMethods, setPackagingMethods] = useState([]);
  const [packagingMethodsLookup, setPackagingMethodsLookup] = useState({});
  const [showCombineIcon, setShowCombineIcon] = useState(false);
  const [showGetDeviceModal, setShowGetDeviceModal] = useState(false);
  const [showPicklistModal, setShowGetPickListModal] = useState(false);
  const [showPrintMedListModal, setShowPrintMedListModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [currentTrayNeeds, setCurrentTrayNeeds] = useState(null);
  const [pendingSortOrder, setPendingSortOrder] = useState("ASC");
  const [routedSortOrder, setRoutedSortOrder] = useState("ASC");
  const [packagedSortOrder, setPackagedSortOrder] = useState("ASC");
  const [devices, setDevices] = useState([]);

  useEffect(() => {
    const matchedDevices = routedDispensingOrders.map((order) => {
      return devices.find((device) => device.name === order.routedToName);
    });

    const uniqueDevices = [
      ...new Map(
        matchedDevices.filter(Boolean).map((device) => [device.name, device])
      ).values(),
    ].sort((a, b) => a.id - b.id);

    setRoutedDevices(uniqueDevices);

    let updatedOrders = routedDispensingOrders.map((o) => ({
      ...o,
      routedToNickname: uniqueDevices.find((d) => d.name === o.routedToName)
        ?.nickname,
    }));
    setJoinedRoutedDispensingOrders(updatedOrders);

    const uniqueDeviceIds = uniqueDevices.map((device) => device.id);
    if (!blockRef.current && !routingRef.current && !refreshingRef.current) {
      // Tick all devices on site selection change
      setRoutedDevicesSelection(uniqueDeviceIds);
    } else {
      setRoutedDevicesSelection((prevSelection) => {
        const filtered = prevSelection.filter((id) => uniqueDeviceIds.includes(id));
        // If nothing left after filtering but devices are available, select all
        return filtered.length > 0 ? filtered : uniqueDeviceIds;
      });
    }
  }, [devices, routedDispensingOrders]);

  useEffect(() => {
    const matchedDevices = packagedDispensingOrders.map((order) => {
      return devices.find((device) => device.name === order.routedToName);
    });

    const uniqueDevices = [
      ...new Map(
        matchedDevices.filter(Boolean).map((device) => [device.name, device])
      ).values(),
    ].sort((a, b) => a.id - b.id);

    setPackagedDevices(uniqueDevices);

    let updatedOrders = packagedDispensingOrders.map((o) => ({
      ...o,
      routedToNickname: uniqueDevices.find((d) => d.name === o.routedToName)
        ?.nickname,
    }));
    setJoinedPackagedDispensingOrders(updatedOrders);

    const uniqueDeviceIds = uniqueDevices.map((device) => device.id);

    if (!blockRef.current && !routingRef.current && !refreshingRef.current) {
      // Tick all devices on site selection change
      setPackagedDevicesSelection(uniqueDeviceIds);
    } else {
      setPackagedDevicesSelection((prevSelection) => {
        const filtered = prevSelection.filter((id) => uniqueDeviceIds.includes(id));
        // If nothing left after filtering but devices are available, select all
        return filtered.length > 0 ? filtered : uniqueDeviceIds;
      });
    }
  }, [devices, packagedDispensingOrders]);

  useEffect(() => {
    // Returning to print med list from facility redirection
    if (location.state?.previousState && sites.length) {
      const index = location.state.previousState.selectedSiteIndex;
      const dispensingOrder = location.state.previousState.dispensingOrder;
      navigate(location.pathname, {});

      fetchData(sites[index]);
      blockRef.current = true;
      DispensingOrderService.getPatients(user, sites[index], dispensingOrder)
        .then((patients) => {
          DispensingOrderService.getMedLists(
            user,
            sites[index],
            dispensingOrder,
            patients.map((patient) => patient._id)
          )
            .then((medLists) => {
              setSelectedSiteIndex(index);
              setPrintDispensingOrder(dispensingOrder);
              setPrintPatients(patients);
              setPrintMedLists(medLists);
              setShowPrintMedListModal(true);
            })
            .catch((error) => {
              setError(error);
              blockRef.current = false;
            });
        })
        .catch((error) => {
          setError(error);
          blockRef.current = false;
        });
      setShowPrintMedListModal(true);
    }
  }, [location, sites]);

  const getName = (dispensingOrder) => {
    if (dispensingOrder.type === "stock") {
      return t("stockOrderLabel", "Stock Order");
    }
    if (dispensingOrder.type === "retail") {
      return dispensingOrder.patientInfo?.name || "Unknown Patient";
    }
    return dispensingOrder.facility?.name || t("invalidFacilityNameError");
  };

  const getSortOrder = (nameA, nameB, orderType) => {
    if (orderType === "ASC") {
      if (nameA < nameB) {
        return -1;
      }
      if (nameB < nameA) {
        return 1;
      }
      return 0;
    } else {
      // case for "DESC"
      if (nameA < nameB) {
        return 1;
      }
      if (nameB < nameA) {
        return -1;
      }
      return 0;
    }
  };

  const fetchData = async (site) => {
    const canViewErrors = RoleCheckService.viewOrdersWithErrors(user);

    setFetchingPending(true);
    setFetchingRouted(true);
    setFetchingPackaged(true);

    try {
      const [orders, trayDataResults] = await Promise.all([
        DispensingOrderService.getDispensingOrdersForSite(user, site, "pending", canViewErrors),
        DispensingOrderService.getRoutedTrays(user, site),
      ]);

      setPendingDispensingOrders(orders.pendingOrders);
      setTrayData(trayDataResults);
      setRoutedDispensingOrders(orders.routedOrders);
      setPackagedDispensingOrders(orders.packagedOrders);

      setFetchingPending(false);
      setFetchingRouted(false);
      setFetchingPackaged(false);
    } catch (e) {
      setFetchingPending(false);
      setFetchingRouted(false);
      setFetchingPackaged(false);
      setError(e);
    }
  };

  useEffect(() => {
    const site = sites[selectedSiteIndex];
    if (!site) return;

    StockLocationService.getDevices(user, site).then((data) => {
      const accessibleDevices = DeviceAccessService.filterDevicesByUserAccess(user, data);

      // User-device filter disabled pending review with Monica
      userPackagingTypesRef.current = new Set();

      const dispensingDevices = accessibleDevices
        .filter((device) => device?.type?.canDispense)
        .sort((a, b) =>
          (a?.nickname || a.name).localeCompare(b?.nickname || b.name)
        )
        .map((device, index) => ({
          id: index + 1,
          name: device.name,
          nickname: device.nickname,
          displayName: device.nickname || device.name,
        }));

      setDevices(dispensingDevices);
    });
  }, [selectedSiteIndex, sites]);

  useEffect(() => {
    refreshingRef.current = false;
    const site = sites[selectedSiteIndex];
    if (site) {
      fetchData(site).then();
    }
    setSelectedPackagingMethods([]);
    setSelectAllPendingOrders(false);
    setPendingDOSelection([]);
    let timerId = setInterval(() => {
      refreshingRef.current = true;
      const site = sites[selectedSiteIndex];
      if (site && !blockRef.current) {
        fetchData(site);
      }
    }, 30000);
    return () => {
      clearInterval(timerId);
    };
  }, [selectedSiteIndex, sites]);

  useEffect(() => {
    PharmacyService.list(user).then((sites) => {
      const enabledSites = sites.filter((site) => site.isEnabled);
      let filteredSites = [];
      if (user.isEnterprise) {
        filteredSites = enabledSites.sort((a, b) => {
          if (a.name.toUpperCase() < b.name.toUpperCase()) {
            return -1;
          }
          if (b.name.toUpperCase() < a.name.toUpperCase()) {
            return 1;
          }
          return 0;
        });
        setSites(filteredSites);
      } else {
        filteredSites = enabledSites
          .filter((site) => user.sites.some((o) => o._id === site._id))
          .map((site) => site);

        filteredSites.sort((a, b) => {
          if (a.name.toUpperCase() < b.name.toUpperCase()) {
            return -1;
          }
          if (b.name.toUpperCase() < a.name.toUpperCase()) {
            return 1;
          }
          return 0;
        });

        setSites(filteredSites);
      }

      if (location.state?.selectedSite) {
        const index = filteredSites.findIndex(
          (o) => o._id === location.state.selectedSite._id
        );
        setSelectedSiteIndex(index);
      } else if (
        dispensingOrdersReturnState &&
        (dispensingOrdersReturnState?.selectedSiteIndex ||
          dispensingOrdersReturnState?.selectedSiteIndex === 0)
      ) {
        setSelectedSiteIndex(dispensingOrdersReturnState.selectedSiteIndex);
      } else if (filteredSites.length > 0) {
        setSelectedSiteIndex(0);
      } else if (sites.length > 0) {
        setSelectedSiteIndex(0);
      }
    });
  }, [user, dispensingOrdersReturnState, location.state?.selectedSite]);

  // SELECT ALL PENDING ORDERS CONTROL
  useEffect(() => {
    // Filter pending orders to only count those that match the selected packaging methods
    const filteredPendingOrders = pendingDispensingOrders.filter((doi) =>
      selectedPackagingMethods.some(
        (o) => o.type === doi.facility?.packagingMethod?.type
      )
    );

    if (
      filteredPendingOrders.length !== 0 &&
      filteredPendingOrders.length === pendingDOSelection.length
    ) {
      handleSelectAllPendingOrders(true);
    } else {
      handleSelectAllPendingOrders(false);
    }
  }, [pendingDOSelection, pendingDispensingOrders, selectedPackagingMethods]);

  // PACKAGING METHODS
  useEffect(() => {
    ConfigService.packagingMethods()
      .then((packagingMethods) => {
        setPackagingMethods(packagingMethods);
      })
      .catch((error) => {
        setError(error);
      });
  }, [user]);

  useEffect(() => {
    if (location.state?.selectedPackagingMethods) {
      setSelectedPackagingMethods(location.state?.selectedPackagingMethods);
    }
      // Limit selectedPackagingMethods to those that match the site devices
    // The packagingMethodsLookup describes which methods are valid for the selected site.
    else if (packagingMethods.length && Object.keys(packagingMethodsLookup)) {
      let tempSelectedPackagingMethods = packagingMethods.filter((obj) => {
        if (packagingMethodsLookup[obj.description]) {
          return obj;
        }
      });
      if (selectedPackagingMethods.length === 0) {
        setSelectedPackagingMethods(tempSelectedPackagingMethods);
      }
    } else {
      setSelectedPackagingMethods([]);
    }
  }, [packagingMethods, packagingMethodsLookup]);

  // COMBINE ORDERS SELECTION
  useEffect(() => {
    setShowCombineIcon(false);

    if (pendingDOSelection.length >= 2) {
      if (pendingDOSelection.every((p) => p.dispensingOrderId.includes("~"))) {
        const values = pendingDOSelection.map(
          (p) => p.dispensingOrderId.split("~")[0]
        );
        const allEquals = values.every((value) => value === values[0]);
        if (allEquals) {
          setShowCombineIcon(true);
        }
      }
    }
  }, [pendingDOSelection]);

  // ACTION MENU
  const handleAction = (action, dispensingOrder, arr) => {
    const temp = [...arr];
    const index = temp.findIndex((o) => o._id === dispensingOrder._id);

    const dispensingOrderId = dispensingOrder.dispensingOrderId;
    const initialDispensingOrderId = dispensingOrderId.split("~")[0];
    const similarDispensingOrders = pendingDispensingOrders.filter(
      (o) =>
        o.dispensingOrderId != dispensingOrderId &&
        o.dispensingOrderId.startsWith(initialDispensingOrderId)
    );

    switch (action.type) {
      case "review":
        navigate("pending", {
          state: {
            user: user,
            site: sites[selectedSiteIndex],
            dispensingOrder: dispensingOrder,
            dispensingOrders: similarDispensingOrders,
            selectedPackagingMethods: selectedPackagingMethods,
          },
        });
        break;
      case "review_routed":
        navigate("routed", {
          state: {
            user: user,
            site: sites[selectedSiteIndex],
            dispensingOrder: dispensingOrder,
            dispensingOrders: similarDispensingOrders,
            selectedPackagingMethods: selectedPackagingMethods,
          },
        });
        break;
      case "review_packaged":
        navigate("packaged", {
          state: {
            user: user,
            site: sites[selectedSiteIndex],
            dispensingOrder: dispensingOrder,
            dispensingOrders: similarDispensingOrders,
            selectedPackagingMethods: selectedPackagingMethods,
          },
        });
        break;
      case "remove":
        if (window.confirm(t("deleteConfirmation"))) {
          blockRef.current = true;
          temp.splice(index, 1);
          DispensingOrderService.resetOrder(
            user,
            sites[selectedSiteIndex],
            dispensingOrder,
            dispensingOrder.state
          )
            .then(() => {
              blockRef.current = false;
            })
            .catch((error) => {
              blockRef.current = false;
              setError(error);
            });

          setPendingDispensingOrders(temp);
        }
        break;
      case "sendToDevice":
        setShowDeviceRouteSelector({ ...dispensingOrder });
        break;
      case "printMedList":
        blockRef.current = true;
        DispensingOrderService.getPatients(
          user,
          sites[selectedSiteIndex],
          dispensingOrder
        )
          .then((patients) => {
            DispensingOrderService.getMedLists(
              user,
              sites[selectedSiteIndex],
              dispensingOrder,
              patients.map((patient) => patient._id)
            )
              .then((medLists) => {
                setPrintDispensingOrder(dispensingOrder);
                setPrintPatients(patients);
                setPrintMedLists(medLists);
                setShowPrintMedListModal(true);
              })
              .catch((error) => {
                setError(error);
                blockRef.current = false;
              });
          })
          .catch((error) => {
            setError(error);
            blockRef.current = false;
          });
        break;
      case "resetToRouted":
        blockRef.current = true;
        temp.splice(index, 1);
        dispensingOrder.state = "routed";
        DispensingOrderService.resetOrder(
          user,
          sites[selectedSiteIndex],
          dispensingOrder,
          dispensingOrder.state
        )
          .then(() => {
            blockRef.current = false;
          })
          .catch((error) => {
            setError(error);
            blockRef.current = false;
          });
        setPackagedDispensingOrders(temp);
        setRoutedDispensingOrders((prev) => {
          return [...prev, dispensingOrder];
        });

        const indexedDevice = devices.find(
          (o) => o.name === dispensingOrder.routedToName
        );
        setRoutedDevices((prev) => {
          if (!prev.some((o) => o.name === indexedDevice.name)) {
            return [...prev, indexedDevice];
          }
          return prev;
        });
        setRoutedDevicesSelection((prev) => {
          if (!prev.includes(indexedDevice.id)) {
            return [...prev, indexedDevice.id];
          }
          return prev;
        });

        break;
      case "resetToPending":
        blockRef.current = true;
        const origState = dispensingOrder.state;
        dispensingOrder.state = "pending";
        temp.splice(index, 1);
        DispensingOrderService.resetOrder(
          user,
          sites[selectedSiteIndex],
          dispensingOrder,
          dispensingOrder.state
        )
          .then(() => {
            blockRef.current = false;
          })
          .catch((error) => {
            setError(error);
            blockRef.current = false;
          });
        if (origState === "routed") {
          setRoutedDispensingOrders(temp);
        } else if (
          origState === "packaged" ||
          origState === "partially_packaged"
        ) {
          setPackagedDispensingOrders(temp);
        }
        setPendingDispensingOrders((prev) => {
          return [...prev, dispensingOrder];
        });
        break;
      default:
        console.log(
          `${action.type} not handled in DispensingOrderController.handleAction`
        );
    }

    setPendingDOSelection([]);
    setRoutedDOSelection([]);
    setPackagedDOSelection([]);
    setVerifiedDOSelection([]);
  };

  // FUNCTIONS
  const handleDoubleClickOrder = (dispensingOrder) => {
    if (dispensingOrder.type === "retail") {
      setOrderDetailsDialog(dispensingOrder);
    }
  };

  // The board reports packaging progress as item counts, not quantities.
  const getProgress = (dispensingOrder) => {
    const progress = dispensingOrder.progress;
    if (!progress?.total) {
      return 0;
    }
    return Math.round((progress.packaged / progress.total) * 100);
  };

  const samePackagingMethods = () => {
    let t = [];
    for (let i = 0; i < pendingDOSelection.length; i++) {
      const type = pendingDOSelection[i].facility?.packagingMethod?.type || "";
      if (
        t.length > 0 &&
        !t.some((o) => {
          return o === type;
        })
      ) {
        return false;
      }
      t.push(type);
    }
    return true;
  };

  const routeSelectedOrdersToDevice = async (device) => {
    const temp = [...pendingDispensingOrders];
    const selectedItems = [...pendingDOSelection];
    setPendingDOSelection([]);

    // sort by priority
    // HIGH, LOW, STAT, WAITER
    const priorityOrder = ["STAT", "WAITER", "HIGH", "LOW"];
    selectedItems.sort((a, b) => {
      return (
        priorityOrder.indexOf(a.priority) - priorityOrder.indexOf(b.priority)
      );
    });

    for (let i = 0; i < selectedItems.length; i++) {
      const dispensingOrder = selectedItems[i];
      if (dispensingOrder) {
        let dispOrder = pendingDispensingOrders.find(
          (o) => o._id === dispensingOrder._id
        );

        const index = temp.findIndex((o) => o._id === dispOrder._id);
        await DispensingOrderService.routeOrder(
          user,
          sites[selectedSiteIndex],
          device,
          dispOrder
        );
        temp.splice(index, 1);
        dispOrder.state = "routed";
        dispOrder.routedToName = device.name;
        setRoutedDispensingOrders((prev) => {
          return [...prev, dispOrder];
        });

        const indexedDevice = devices.find(
          (device) => device.name === dispOrder.routedToName
        );
        setRoutedDevices((prev) => {
          if (!prev.some((o) => o.name === indexedDevice.name)) {
            return [...prev, indexedDevice];
          }
          return prev;
        });

        setRoutedDevicesSelection((prev) => {
          if (!prev.includes(indexedDevice.id)) {
            return [...prev, indexedDevice.id];
          }
          return prev;
        });
      }
    }
    setPendingDispensingOrders(temp);
  };

  const handleClickOrder = (dispensingOrder, e) => {
    let arr = [];
    switch (dispensingOrder.state) {
      case "pending":
        arr = [...pendingDOSelection];
        arr = modifyArray(arr, dispensingOrder);
        setPendingDOSelection(arr);
        setRoutedDOSelection([]);
        setPackagedDOSelection([]);
        setVerifiedDOSelection([]);
        break;
      case "routed":
        arr = [...routedDOSelection];
        arr = modifyArray(arr, dispensingOrder);
        setRoutedDOSelection(arr);
        setPendingDOSelection([]);
        setPackagedDOSelection([]);
        setVerifiedDOSelection([]);
        break;
      case "packaged":
      case "partially_packaged":
        arr = [...packagedDOSelection];
        arr = modifyArray(arr, dispensingOrder);
        setPackagedDOSelection(arr);
        setPendingDOSelection([]);
        setRoutedDOSelection([]);
        setVerifiedDOSelection([]);
        break;
      case "verified":
        arr = [...verifiedDOSelection];
        arr = modifyArray(arr, dispensingOrder);
        setVerifiedDOSelection(arr);
        setPendingDOSelection([]);
        setRoutedDOSelection([]);
        setPackagedDOSelection([]);
        break;
      default:
        break;
    }
  };

  const modifyArray = (arr, dispensingOrder) => {
    if (arr.some((item) => item._id === dispensingOrder._id)) {
      const index = arr.findIndex((item) => {
        return item._id === dispensingOrder._id;
      });

      if (index !== -1) {
        arr.splice(index, 1);
      }
    } else {
      arr.push(dispensingOrder);
    }
    return arr;
  };

  const handlePackagingMethodsLookup = (lookupObject) => {
    return setPackagingMethodsLookup(lookupObject);
  };

  const handleTrayNeeds = async (dispensingOrder) => {
    setCurrentTrayNeeds(dispensingOrder);
  };

  // UI COMPONENTS
  const dispensingOrderQueueHeaderGroup = (state) => {
    return (
      <div className={styles.dispensingOrderQueueHeaderIcons}>
        {RoleCheckService.viewOrderPickList(user) &&
          ((state === "pending" && pendingDOSelection.length > 0) ||
            (state === "routed" && routedDOSelection.length > 0)) &&
          pendingDOSelection.length > 0 && (
            <div className={styles.dispensingOrderQueueHeaderIcon}>
              <FontAwesomeIcon
                icon={faClipboardList}
                color={"#FFAB2B"}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setShowGetDeviceModal(true);
                }}
              />
            </div>
          )}
        {RoleCheckService.isSuperAdmin(user) && state === "pending" && (
          <div className={styles.dispensingOrderQueueHeaderIcon}>
            <FontAwesomeIcon
              icon={faUserSecret}
              color={"#562F7E"}
              style={{ cursor: "pointer" }}
              onClick={() => {
                navigate("order-generation", {
                  state: {
                    user: user,
                    site: sites[selectedSiteIndex],
                    selectedPackagingMethods: selectedPackagingMethods,
                  },
                });
              }}
            />
          </div>
        )}
        {RoleCheckService.canCreateScripts(user) && state === "pending" && (
          <div className={styles.dispensingOrderQueueHeaderIcon}>
            <FontAwesomeIcon
              icon={faPrescriptionBottle}
              color={"#FFAB2B"}
              style={{ cursor: "pointer" }}
              onClick={() => {
                navigate("retail-script", {
                  state: {
                    user: user,
                    site: sites[selectedSiteIndex],
                    selectedPackagingMethods: selectedPackagingMethods,
                  },
                });
              }}
            />
          </div>
        )}
        {state === "pending" && (
          <div className={styles.dispensingOrderQueueHeaderIcon}>
            {showCombineIcon && (
              <FontAwesomeIcon
                icon={faCodeMerge}
                color={"#FFAB2B"}
                style={{ cursor: "pointer" }}
                onClick={async () => {
                  setFetchingPending(true);
                  await DispensingOrderService.combineOrders(
                    user,
                    pendingDOSelection
                  );
                  let arr = [...pendingDispensingOrders];
                  let index = arr.findIndex(
                    (o) => o._id === pendingDOSelection[0]._id
                  );
                  arr.splice(index, 1);
                  index = arr.findIndex(
                    (o) => o._id === pendingDOSelection[1]._id
                  );
                  arr.splice(index, 1);
                  setPendingDispensingOrders(arr);
                  setPendingDOSelection([]);
                  const site = sites[selectedSiteIndex];
                  fetchData(site);
                }}
              />
            )}
          </div>
        )}

        <div className={styles.dispensingOrderQueueHeaderIcon}>
          <FontAwesomeIcon
            icon={faSort}
            color={"#FFAB2B"}
            style={{ cursor: "pointer" }}
            onClick={() => {
              if (state === "pending") {
                setPendingSortOrder((prev) => {
                  if (prev === "ASC") {
                    return "DESC";
                  }
                  return "ASC";
                });
              } else if (state === "routed") {
                setRoutedSortOrder((prev) => {
                  if (prev === "ASC") {
                    return "DESC";
                  }
                  return "ASC";
                });
              } else if (state === "packaged") {
                setPackagedSortOrder((prev) => {
                  if (prev === "ASC") {
                    return "DESC";
                  }
                  return "ASC";
                });
              }
            }}
          />
        </div>
      </div>
    );
  };

  const selectDeviceComponent = () => {
    return (
      <ModalGen modalClassName={styles.modal}>
        <SelectDevice
          sites={sites}
          selectedSiteIndex={selectedSiteIndex}
          selectedDevice={selectedDevice}
          onHandleSetSelectedDevice={(str) => {
            setSelectedDevice(str);
          }}
          onHandleCloseGenPickListModal={() => {
            setShowGetDeviceModal(false);
            setPendingDOSelection([]);
          }}
          onHandleGeneratePickList={() => {
            setShowGetDeviceModal(false);
            setShowGetPickListModal(true);
          }}
          onHandleModal={(boolVal) => {
            setShowGetDeviceModal(boolVal);
            setSelectedDevice("");
          }}
        />
      </ModalGen>
    );
  };

  const getPickList = () => {
    if (RoleCheckService.viewOrderPickList(user) && showPicklistModal) {
      return (
        <PickList
          user={user}
          dispensingOrdersSelected={pendingDOSelection}
          selectedDevice={selectedDevice}
          onHandleSetSelectedDevice={(str) => {
            setSelectedDevice(str);
            setShowGetPickListModal(false);
          }}
          onHandleSetDispensingOrdersSelected={(arr) => {
            setPendingDOSelection(arr);
          }}
          onHandleSetActiveChildComponent={(str) => {}}
        />
      );
    }
  };

  const getPrintMedList = () => {
    // console.log("getPrintMedList");
    if (RoleCheckService.viewOrderDetails(user) && showPrintMedListModal) {
      return (
        <ModalGen modalClassName={styles.modal}>
          <PrintMedList
            user={user}
            patients={printPatients}
            medLists={printMedLists}
            selectedSiteIndex={selectedSiteIndex}
            dispensingOrder={printDispensingOrder}
            onHandleClosePrintMedListModal={() => {
              setShowPrintMedListModal(false);
              setPrintDispensingOrder(null);
              setPrintPatients([]);
              setPrintMedLists([]);
              blockRef.current = false;
            }}
            onHandleModal={(boolVal) => {
              setShowPrintMedListModal(boolVal);
              if (!boolVal) {
                setPrintDispensingOrder(null);
                setPrintPatients([]);
                setPrintMedLists([]);
                blockRef.current = false;
              }
            }}
          />
        </ModalGen>
      );
    }
  };

  const getSiteHeader = (site, index) => {
    const isSelected = selectedSiteIndex === index;
    return (
      <SiteHeader
        siteIsSelected={isSelected}
        key={site._id}
        site={site}
        onSelect={() => setSelectedSiteIndex(index)}
        // onSelect2={smScreen ? handleVerifySelectionClicked() : () => {}}
        onSelect2={smScreen ? () => {} : () => {}}
        user={user}
        isSelected={isSelected}
        siteReplenishmentNeeds={[]}
        onDeviceReplenishment={(deviceNeed) => {
          const stockLocation = deviceNeed.stockLocation;
          navigate("/devices", {
            state: {
              stockLocation: stockLocation,
            },
          });
        }}
      />
    );
  };

  const filterOrder = (filterText, o) => {
    const filter = filterText?.toUpperCase();
    const { dispensingOrderItems, facility, dispensingOrderId } = o;


    const matchesFilterText =
      !filter ||
      dispensingOrderId?.toUpperCase()?.includes(filter) ||
      facility?.name?.toUpperCase()?.includes(filter) ||
      dispensingOrderItems?.some(
        (i) =>{
          return(
            i.rxNumber?.toUpperCase()?.includes(filter) ||
            i.patientInfo?.name?.toUpperCase()?.includes(filter))
        }
      );
    const isStockOrder = o.type === "stock";
    const matchesPackagingMethod = isStockOrder || selectedPackagingMethods.some(
      (pm) => {
        return pm.type === facility?.packagingMethod?.type;
      }
    );

    return matchesFilterText && matchesPackagingMethod;
  };

  const getPendingColumn = (filterText) => {
    return (
      <div className={styles.dispensingOrderQueue}>
        <div className={styles.dispensingOrderQueueHeader}>
          <Checkbox
            labelName={""}
            isOn={selectAllPendingOrders}
            onCheck={(isOn) => {
              if (!isOn) {
                setPendingDOSelection([]);
              } else {
                let arr = [];
                // check filter and only select if filter is on
                for (let i = 0; i < pendingDispensingOrders.length; i++) {
                  const doi = pendingDispensingOrders[i];
                  if (
                    doi.type === "stock" ||
                    selectedPackagingMethods.some(
                      (o) => o.type === doi.facility?.packagingMethod?.type
                    )
                  ) {
                    arr.push(doi);
                  }
                }
                setPendingDOSelection(arr);
              }
              setSelectAllPendingOrders(isOn);
            }}
          />
          <p className={styles.dispensingOrderQueueTitle}>
            {t("pendingHeader")}
          </p>
          {fetchingPending && (
            <Rings color="#089BAB" height={50} width={50} />
          )}
          {dispensingOrderQueueHeaderGroup("pending")}
        </div>
        {/*    Facilities go here   */}

        <div
          id={"facilities"}
          className={styles.facilityColumn}
          ref={facilitiesEl}
        >
          {pendingDispensingOrders
            .filter((o) => {
              const test = filterOrder(filterText, o);
              return test;
            })
            .sort((a, b) => {
              let nameA = getName(a);
              let nameB = getName(b);
              return getSortOrder(nameA, nameB, pendingSortOrder);
            })
            .map((dispensingOrder, index) => {
              // Do not assign the value for the map key to the array index.  This may cause a problem when a card is deleted (like from a combineOrder operation).  Assign a unique id to the key instead.
              return (
                <FacilityDispensingOrdersCard
                  user={user}
                  dispensingOrder={dispensingOrder}
                  onDrag={(e, dispensingOrder) => {
                    e.preventDefault();
                    setDraggedDispensingOrders(pendingDOSelection);
                  }}
                  key={dispensingOrder.dispensingOrderId}
                  trayNeeds={[]}
                  onTrayNeeds={(trayNeeds) => {}}
                  patientCount={dispensingOrder.patientCount}
                  bagCount={dispensingOrder.qtyNeeded}
                  alertCount={0}
                  dueTime={0}
                  progress={0}
                  onAction={(action) => {
                    handleAction(
                      action,
                      dispensingOrder,
                      pendingDispensingOrders
                    );
                  }}
                  onClickOrder={handleClickOrder}
                  onDoubleClickOrder={() => {
                    handleDoubleClickOrder(dispensingOrder);
                  }}
                  isSelected={pendingDOSelection.some((o) => {
                    return o._id === dispensingOrder._id;
                  })}
                />
              );
            })}
        </div>
      </div>
    );
  };

  const getRoutedDeviceFilter = () => {
    const filteredRoutedDevices = routedDevices.filter((device) => {
      return joinedRoutedDispensingOrders.some((order) => {
        return (
          order.routedToNickname === device.nickname &&
          (order.type === "stock" || selectedPackagingMethods.some(
            (pm) => pm.type === order.facility?.packagingMethod?.type
          ))
        );
      });
    });

    return (
      <div className={styles.MultiSelectDropDownContainer}>
        <MultiSelectDropDown
          value={routedDevicesSelection}
          options={filteredRoutedDevices}
          fieldNames={{ value: "id", label: "displayName" }}
          onChanged={setRoutedDevicesSelection}
          asFunnelIcon={true}
          selectAllLabel={t("selectAllDevices")}
          unselectAllLabel={t("unselectAllDevices")}
          noContentLabel={t("noRecordsFilter")}
        />
      </div>
    );
  };

  const getRoutedColumn = (filterText) => {
    const selectedSite = selectedSiteIndex !== null && sites[selectedSiteIndex] ? sites[selectedSiteIndex] : null;
    return (
      <div className={styles.dispensingOrderQueue}>
        <div className={styles.dispensingOrderQueueHeader}>
          <div className={styles.titleAndFilter}>
            <p className={styles.dispensingOrderQueueTitle}>
              {t("routedHeader")}
            </p>
            {getRoutedDeviceFilter()}
          </div>
          <div className={styles.loaderAndHeaderGroup}>
            {fetchingRouted && (
              <Rings color="#089BAB" height={50} width={50} />
            )}
            {dispensingOrderQueueHeaderGroup("routed")}
          </div>
        </div>
        {/*    Facilities go here   */}
        <div
          id={"facilities"}
          className={styles.facilityColumn}
          ref={facilitiesEl}
        >
          {joinedRoutedDispensingOrders
            .filter((o) => {
              return (
                (routedDevicesSelection !== null
                  ? routedDevices
                    .filter((device) => {
                        return routedDevicesSelection.includes(device.id);
                      }
                    )
                    .map((device) => {
                      return device.name;
                    })
                    .includes(o.routedToName)
                  : true) && filterOrder(filterText, o)
              );
            })
            .sort((a, b) => {
              let nameA = getName(a);
              let nameB = getName(b);
              return getSortOrder(nameA, nameB, routedSortOrder);
            })
            .map((dispensingOrder, index) => {
              /*************************************************************************/
              // Added by RWD on 12/27/2023
              let trayDataObj = null;
              const dispensingOrderTrayDataIndex = trayData.findIndex(
                (trayDataObj) => {
                  return (
                    trayDataObj?.dispensingOrder?._id === dispensingOrder?._id
                  );
                }
              );

              if (dispensingOrderTrayDataIndex !== -1) {
                trayDataObj = trayData[dispensingOrderTrayDataIndex];
              }
              /*************************************************************************/

              // Do not assign the value for the map key to the array index.  This may cause a problem when a card is deleted (like from a combineOrder operation).  Assign a unique id to the key instead.

              return (
                <FacilityDispensingOrdersCard
                  user={user}
                  dispensingOrder={dispensingOrder}
                  site={selectedSite}
                  trayDataObj={trayDataObj}
                  onDrag={() => {}}
                  key={dispensingOrder.dispensingOrderId}
                  trayNeeds={dispensingOrder.trayCount}
                  onTrayNeeds={() => {
                    handleTrayNeeds(dispensingOrder);
                  }}
                  patientCount={dispensingOrder.patientCount}
                  bagCount={dispensingOrder.qtyNeeded}
                  alertCount={0}
                  dueTime={0}
                  progress={0}
                  onAction={(action) => {
                    handleAction(
                      action,
                      dispensingOrder,
                      joinedRoutedDispensingOrders
                    );
                  }}
                  onClickOrder={() => {}}
                  onDoubleClickOrder={() => {
                    handleDoubleClickOrder(dispensingOrder);
                  }}
                  isSelected={false}
                />
              );
            })}
        </div>
      </div>
    );
  };

  const getPackagedDeviceFilter = () => {
    // Filter devices to show only those that match selected packaging methods
    const filteredPackagedDevices = packagedDevices.filter(device => {
      // Check if any packaged order for this device matches the selected packaging methods
      return joinedPackagedDispensingOrders.some(order =>
        order.routedToName === device.name &&
        (order.type === "stock" || selectedPackagingMethods.some(pm =>
          pm.type === order.facility?.packagingMethod?.type
        ))
      );
    });

    return (
      <div className={styles.MultiSelectDropDownContainer}>
        <MultiSelectDropDown
          value={packagedDevicesSelection}
          options={filteredPackagedDevices}
          fieldNames={{ value: "id", label: "displayName" }}
          onChanged={setPackagedDevicesSelection}
          asFunnelIcon={true}
          selectAllLabel={t("selectAllDevices")}
          unselectAllLabel={t("unselectAllDevices")}
          noContentLabel={t("noRecordsFilter")}
        />
      </div>
    );
  };

  const getPackagedColumn = (filterText) => {
    return (
      <div className={styles.dispensingOrderQueue}>
        <div className={styles.dispensingOrderQueueHeader}>
          <div className={styles.titleAndFilter}>
            <p className={styles.dispensingOrderQueueTitle}>
              {t("packagedHeader")}
            </p>
            {getPackagedDeviceFilter()}
          </div>

          <div className={styles.loaderAndHeaderGroup}>
            {fetchingPackaged && (
              <Rings color="#089BAB" height={50} width={50} />
            )}
            {dispensingOrderQueueHeaderGroup("packaged")}
          </div>
        </div>
        {/*    Facilities go here   */}
        <div
          id={"facilities"}
          className={styles.facilityColumn}
          ref={facilitiesEl}
        >
          {joinedPackagedDispensingOrders
            .filter((o) => {
              return (
                (packagedDevicesSelection !== null
                  ? packagedDevices
                    .filter((device) => {
                        return packagedDevicesSelection.includes(device.id);
                      }
                    )
                    .map((device) => device.name)
                    .includes(o.routedToName)
                  : true) && filterOrder(filterText, o)
              );
            })
            .sort((a, b) => {
              let nameA = getName(a);
              let nameB = getName(b);
              return getSortOrder(nameA, nameB, packagedSortOrder);
            })
            .map((dispensingOrder, index) => {
              // Do not assign the value for the map key to the array index.  This may cause a problem when a card is deleted (like from a combineOrder operation).  Assign a unique id to the key instead.
              return (
                <FacilityDispensingOrdersCard
                  user={user}
                  onDrag={() => {}}
                  key={dispensingOrder.dispensingOrderId}
                  trayNeeds={[]}
                  onTrayNeeds={(trayNeeds) => {}}
                  dispensingOrder={dispensingOrder}
                  patientCount={dispensingOrder.patientCount}
                  bagCount={dispensingOrder.qtyNeeded}
                  alertCount={0}
                  dueTime={0}
                  progress={getProgress(dispensingOrder)}
                  onAction={(action) => {
                    handleAction(
                      action,
                      dispensingOrder,
                      joinedPackagedDispensingOrders
                    );
                  }}
                  onClickOrder={() => {}}
                  onDoubleClickOrder={() => {
                    handleDoubleClickOrder(dispensingOrder);
                  }}
                  isSelected={false}
                  hasSubstitute={dispensingOrder.hasSubstitute}
                  onSubstituteReport={(trayNeeds) => {
                    navigate("/reports", {
                      state: {
                        dataFromDispensingOrders: {
                          returnURL: "/dispensing-orders",
                          customizedReport: "Substitute",
                          selectedSiteIndex:
                            selectedSiteIndex || selectedSiteIndex === 0
                              ? selectedSiteIndex
                              : null,
                          dispensingOrderId:
                          dispensingOrder.dispensingOrderId,
                          createdAt: dispensingOrder.createdAt,
                          packagedOn: dispensingOrder.packagedOn,
                        },
                      },
                    });
                    // console.log(`run sub report for ${dispensingOrder.dispensingOrderId}`)
                  }}
                />
              );
            })}
        </div>
      </div>
    );
  };

  const getPackagingMethodWidget = () => {
    return (
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <PackagingMethodFilter
          packagingMethodsLookup={packagingMethodsLookup}
          handlePackagingMethodsLookup={handlePackagingMethodsLookup}
          selectedSite={sites[selectedSiteIndex]}
          packagingMethods={packagingMethods}
          selectedPackagingMethods={selectedPackagingMethods}
          user={user}
          onChange={(temp) => {
            setSelectedPackagingMethods(temp);
          }}
          siteIsSelected={selectedSiteIndex != null}
        />
      </div>
    );
  };

  const getDeviceCoverageWidget = () => {
    // Check if any selected order has an error
    const hasErrorOrders = pendingDOSelection.some((order) => order.errorExists);

    // Don't show device icons if any selected order has an error
    if (hasErrorOrders) {
      return null;
    }

    if (samePackagingMethods()) {
      return (
        <DeviceCoverage
          user={user}
          site={sites[selectedSiteIndex]}
          dispensingOrder={pendingDOSelection[0]}
          showBlisterPackagers={selectedPackagingMethods.some((o) => {
            return o.type === "BLISTER_CARD";
          })}
          showPouchPackagers={selectedPackagingMethods.some((o) => {
            return o.type === "POUCH";
          })}
          showVialFillers={selectedPackagingMethods.some((o) => {
            return o.type === "VIAL";
          })}
          onDrop={async (e, device) => {
            e.stopPropagation(); // Stops some browsers from redirecting.
            e.preventDefault();

            // Additional safety check: don't route orders with errors
            const ordersWithErrors = draggedDispensingOrders.filter((order) => order.errorExists);
            if (ordersWithErrors.length > 0) {
              setError(t("cannotRouteOrdersWithErrors"));
              setDraggedDispensingOrders([]);
              return;
            }

            const temp = [...pendingDispensingOrders];
            for (let i = 0; i < draggedDispensingOrders.length; i++) {
              const draggedDispensingOrder = draggedDispensingOrders[i];
              if (
                draggedDispensingOrder &&
                RoleCheckService.routeOrders(user)
              ) {
                let dispOrder = pendingDispensingOrders.find(
                  (o) => o._id === draggedDispensingOrder._id
                );
                const index = temp.findIndex((o) => o._id === dispOrder._id);
                await DispensingOrderService.routeOrder(
                  user,
                  sites[selectedSiteIndex],
                  device,
                  dispOrder
                );
                temp.splice(index, 1);
                dispOrder.state = "routed";
                dispOrder.routedToName = device.name;
                setRoutedDispensingOrders((prev) => {
                  return [...prev, dispOrder];
                });
              }
            }
            setPendingDispensingOrders(temp);
            setDraggedDispensingOrders([]);
            setPendingDOSelection([]);
          }}
          onClick={async (device) => {
            // Additional safety check: don't route orders with errors
            const ordersWithErrors = pendingDOSelection.filter((order) => order.errorExists);
            if (ordersWithErrors.length > 0) {
              setError(t("cannotRouteOrdersWithErrors"));
              return;
            }

            if (RoleCheckService.routeOrders(user)) {
              await routeSelectedOrdersToDevice(device);
            }
          }}
        />
      );
    }
  };

  const getDeviceRouteSelector = () => {
    return (
      <DeviceRouteSelector
        user={user}
        dispensingOrder={showDeviceRouteSelector}
        site={sites[selectedSiteIndex]}
        onRoute={async (dispensingOrder, device) => {
          routingRef.current = true;

          try {
            const temp = [...pendingDispensingOrders];
            const index = temp.findIndex((o) => o._id === dispensingOrder._id);
            DispensingOrderService.routeOrder(
              user,
              sites[selectedSiteIndex],
              device,
              dispensingOrder
            ).then();
            setShowDeviceRouteSelector(null);
            temp.splice(index, 1);
            setPendingDispensingOrders(temp);
            dispensingOrder.state = "routed";
            dispensingOrder.routedToName = device.name;

            setRoutedDispensingOrders((prev) => {
              return [...prev, dispensingOrder];
            });

            const indexedDevice = devices.find((o) => o.name === device.name);
            setRoutedDevices((prev) => {
              if (!prev.some((o) => o.name === indexedDevice.name)) {
                return [...prev, indexedDevice];
              }
              return prev;
            });
            setRoutedDevicesSelection((prev) => {
              if (!prev.includes(indexedDevice.id)) {
                return [...prev, indexedDevice.id];
              }
              return prev;
            });
          } catch (error) {
            notification.error({
              message: t("routingValidationFailed"),
              description: error?.message || error || t("routingError"),
              duration: 6,
            });
            setShowDeviceRouteSelector(null);
          } finally {
            routingRef.current = false;
          }
        }}
        onClose={() => {
          setShowDeviceRouteSelector(null);
        }}
      />
    );
  };

  const handleError = (err) => {
    setError(err);
  };

  const handlePendingDOSelection = (selection) => {
    setPendingDOSelection(selection);
  };

  const handleSelectAllPendingOrders = (boolVal) => {
    setSelectAllPendingOrders(boolVal);
  };

  const handleOrderDetailsDialog = (details) => {
    setOrderDetailsDialog(details);
  };

  if (smScreen) {
    return (
      <DispensingOrdersMobileView
        sites={sites}
        error={error}
        onError={handleError}
        getSiteHeader={getSiteHeader}
        dialogRef={dialogRef}
        dispensingOrderQueueHeaderGroup={dispensingOrderQueueHeaderGroup}
        getPendingColumn={getPendingColumn}
        getRoutedColumn={getRoutedColumn}
        getPackagedColumn={getPackagedColumn}
        getPackagingMethodWidget={getPackagingMethodWidget}
        getDeviceCoverageWidget={getDeviceCoverageWidget}
        productFieldControllerRef={productFieldControllerRef}
        selectedSiteIndex={selectedSiteIndex}
        fetchingPending={fetchingPending}
        pendingDispensingOrders={pendingDispensingOrders}
        pendingDOSelection={pendingDOSelection}
        onPendingDOSelection={handlePendingDOSelection}
        selectAllPendingOrders={selectAllPendingOrders}
        onSelectAllPendingOrders={handleSelectAllPendingOrders}
        orderDetailsDialog={orderDetailsDialog}
        handleOrderDetailsDialog={handleOrderDetailsDialog}
        showDeviceRouteSelector={showDeviceRouteSelector}
        getDeviceRouteSelector={getDeviceRouteSelector}
        showGetDeviceModal={showGetDeviceModal}
        selectDeviceComponent={selectDeviceComponent}
        showPicklistModal={showPicklistModal}
        getPickList={getPickList}
        getRoutedDeviceFilter={getRoutedDeviceFilter}
        getPackagedDeviceFilter={getPackagedDeviceFilter}
        showPrintMedListModal={showPrintMedListModal}
        getPrintMedList={getPrintMedList}
      />
    );
  } else if (currentTrayNeeds) {
    return (
      <TrayPickList
        user={user}
        data={currentTrayNeeds}
        onClose={() => setCurrentTrayNeeds(null)}
      />
    );
  } else {
    return (
      <DispensingOrdersDesktopView
        sites={sites}
        t={t}
        error={error}
        onError={handleError}
        getSiteHeader={getSiteHeader}
        dialogRef={dialogRef}
        getPendingColumn={getPendingColumn}
        getRoutedColumn={getRoutedColumn}
        getPackagedColumn={getPackagedColumn}
        getPackagingMethodWidget={getPackagingMethodWidget}
        getDeviceCoverageWidget={getDeviceCoverageWidget}
        productFieldControllerRef={productFieldControllerRef}
        pendingDOSelection={pendingDOSelection}
        orderDetailsDialog={orderDetailsDialog}
        handleOrderDetailsDialog={handleOrderDetailsDialog}
        showDeviceRouteSelector={showDeviceRouteSelector}
        getDeviceRouteSelector={getDeviceRouteSelector}
        showGetDeviceModal={showGetDeviceModal}
        selectDeviceComponent={selectDeviceComponent}
        showPicklistModal={showPicklistModal}
        getPickList={getPickList}
        showPrintMedListModal={showPrintMedListModal}
        getPrintMedList={getPrintMedList}
      />
    );
  }
};
export default DispensingOrders;
